/* eslint-disable no-param-reassign */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from '../../../juristec-ui/core/SelectNew';
import SelectTips from '../../Popovers/SelectTips';
import KpiFilter from '../../KpiFilter';
import Button from '../../../juristec-ui/core/Button';
import KpiFilterApplied from '../../KpiFilterApplied';
import InputTextLine from '../../../juristec-ui/core/InputTextLine';
import { trimString } from '../../../juristec-ui/utils/functions/formatString';
import { verifyInput } from '../../../juristec-ui/utils/validators/inputTextValidators';
import { dateOptions } from '../../../options';
import { SelectOption } from '../../../types';

import {
  useModifierConfig,
  optionNamespace,
  GroupOptions,
  TypePeriodOptions,
  type ModifierGroup,
  type ModifierConfig,
  type DataFrameTypes,
  type Value,
} from '../../../hooks/modifiers';

import {
  ModifColumn, ModifRow, ModifsContainer, LabelSmall,
} from './styled/SubsetTableCreate.styled';

interface Modifier {
  modifiers: ModifierGroup;
  origin: 'filter' | 'group';
  columnOpts?: SelectOption[];
  addModifier: (modifierConfig: ModifierConfig) => void;
  removeModifier: (origin: string, uid: string) => void;
  editModifier: (
    origin: string, uid: string, newValues: Value[], fType: string, newRule?: string,
  ) => void;
  getUniqueValues: (column: string, selector: string) => Promise<SelectOption[]>;
  changeModifierOrder: (origin: string, pos: number, varOp: 1|-1) => void;
  dataFrameTypes: DataFrameTypes;
  atModal: boolean;
}

interface GroupNameState {
  value: string;
  error: boolean;
  errorMsg: string;
}

const emptyArr: SelectOption[] = [];

const Modifier = ({
  modifiers,
  origin = 'filter',
  columnOpts = emptyArr,
  addModifier,
  removeModifier,
  editModifier,
  getUniqueValues,
  changeModifierOrder,
  dataFrameTypes,
  atModal,
}: Modifier) => {
  const { t } = useTranslation();

  const [groupName, setGroupName] = useState<GroupNameState>({ value: '', error: true, errorMsg: '' });

  const {
    modifierConfig,
    simpleOptions,
    getColumnOptions,
    getSimpleOptions,
    handleModifierConfigs,
    resetModifierConfigs,
    //
    configColumnIsDate,
    getOptionsByConfigGroup,
    checkModifierValues,
  } = useModifierConfig({
    dataFrameTypes,
    modifierOrigins: { [origin]: origin },
    getUniqueValues,
  });

  const handleColumnSelect = (selected: SelectOption) => handleModifierConfigs({
    column: selected,
  });

  const handleGroupSelect = (selected: SelectOption) => handleModifierConfigs({
    group: selected,
  });

  const handleSelectorSelect = (selected: SelectOption) => handleModifierConfigs({
    selector: selected,
  });

  const handleFormatSelect = (selected: SelectOption) => handleModifierConfigs({
    format: selected,
  });

  const handleGroupChange = (values: Value[]) => handleModifierConfigs({ values });

  const handleGroupName = (value: string) => {
    const msg = verifyInput(value, true);
    setGroupName({
      value,
      error: msg.length !== 0,
      errorMsg: msg,
    });
    if (msg.length === 0) {
      handleModifierConfigs({ rule: value });
    }
  };

  const handleAddModifier = () => {
    addModifier(modifierConfig);
    setGroupName({ value: '', error: true, errorMsg: '' });
    resetModifierConfigs();
  };

  return (
    <>
      <ModifColumn>
        <ModifRow>
          <div>
            <Select
              label={origin === 'filter' ? 'Filtrar por' : 'Agrupar por'}
              onChange={handleColumnSelect}
              // @ts-expect-error not typed yet
              value={modifierConfig.column}
              options={getColumnOptions({ [origin]: columnOpts })}
              sortBy="ascending"
              searchable
              clearable
              atModal
            />
          </div>
          <div>
            <Select
              label="Tipo"
              onChange={handleGroupSelect}
              // @ts-expect-error not typed yet
              value={modifierConfig.group}
              options={GroupOptions.list}
              optionNamespace={optionNamespace}
              atModal
            />
          </div>
        </ModifRow>
        {(modifierConfig.column?.value?.length ?? 0) > 0 && (
          <>
            <ModifRow>
              {modifierConfig.group.value === GroupOptions.enum.SIMPLE
              && configColumnIsDate() && (
                <div>
                  <Select
                    label="Formato"
                    placeholder="Selecione"
                    onChange={handleSelectorSelect}
                    // @ts-expect-error not typed yet
                    value={modifierConfig.selector}
                    options={dateOptions}
                    searchable
                    atModal
                  />
                </div>
              )}
              <div style={{ position: 'relative' }}>
                <Select
                  label="Condição"
                  onChange={handleFormatSelect}
                  // @ts-expect-error not typed yet
                  value={modifierConfig.format}
                  options={getOptionsByConfigGroup()}
                  optionNamespace={optionNamespace}
                  searchable
                  atModal
                />
                {modifierConfig.format.value !== '' && (
                  <SelectTips
                    // @ts-expect-error not typed yet
                    description={t(modifierConfig.format.description, {
                      ns: optionNamespace,
                      ...(modifierConfig.format.params ?? {}),
                    })}
                    style={{ top: '5px', right: '5px' }}
                  />
                )}
              </div>
            </ModifRow>
            {modifierConfig.format?.value.length > 0 && (
              modifierConfig.group?.value === GroupOptions.enum.ADVANCED || simpleOptions.length > 0
            ) && (
              <>
                {!Object.keys(TypePeriodOptions.map).includes(modifierConfig.format.value) && (
                  <KpiFilter
                    filterType={modifierConfig.format.value}
                    // @ts-expect-error not typed yet
                    handle={handleGroupChange}
                    // @ts-expect-error not typed yet
                    values={modifierConfig.values}
                    // @ts-expect-error not typed yet
                    options={getSimpleOptions(modifiers)}
                    format={modifierConfig.column?.type}
                    atModal={atModal}
                  />
                )}
                {origin === 'group' && (
                  <InputTextLine
                    label="Nome do Agrupamento"
                    type="text"
                    style={{ marginBottom: 0 }}
                    onChange={(e) => handleGroupName(e.target.value)}
                    value={groupName.value}
                    errorMessage={groupName.errorMsg}
                    error={groupName.errorMsg.length > 0}
                    styleContainer={{ marginTop: '-8px' }}
                    onBlur={(e) => handleGroupName(trimString(e.target.value))}
                    setValue={() => {}}
                  />
                )}
                <Button
                  onClick={handleAddModifier}
                  color="secondary"
                  disabled={!checkModifierValues() || (
                    origin === 'group' && groupName.error
                  )}
                  style={{ margin: '10px 0 10px', width: '100%' }}
                >
                  {origin === 'filter' ? 'Adicionar filtro' : 'Adicionar agrupamento'}
                </Button>
              </>
            )}
          </>
        )}
      </ModifColumn>
      {modifiers[origin].length > 0 && (
        <ModifsContainer>
          <LabelSmall>
            {origin === 'filter' ? 'Filtros Aplicados' : 'Agrupamentos Aplicados'}
          </LabelSmall>
          {modifiers[origin].map((item, i) => (
            <KpiFilterApplied
              nameEdit={origin === 'group'}
              key={item.uid}
              origin={origin}
              modifier={item}
              modifiers={modifiers}
              removeFunc={removeModifier}
              editFunc={editModifier}
              getOptions={getUniqueValues}
              dataFrameTypes={dataFrameTypes}
              position={i}
              isLast={modifiers[origin].length - 1 === i}
              changePosition={changeModifierOrder}
            />
          ))}
        </ModifsContainer>
      )}
    </>
  );
};

export default Modifier;
